import { useRef, useState, useEffect } from 'react';
import { motion, useAnimation, useMotionValue, PanInfo } from 'framer-motion';

const tools = [
  {
    name: 'Petição Inicial',
    description: 'Redija sua Petição Inicial com base em fatos e evidências.',
  },
  {
    name: 'Contestação',
    description: 'Gere uma Contestação a partir de uma Petição Inicial.',
  },
  {
    name: 'Recursos',
    description: 'Crie um Recurso com base em uma Decisão ou Sentença.',
  },
  {
    name: 'Contrarrazões',
    description: 'Elabore Contrarrazões para um Recurso apresentado.',
  },
  {
    name: 'Réplica',
    description: 'Gere uma Réplica com base na Petição Inicial e Contestação.',
  },
  {
    name: 'Impugnação',
    description: 'Produza uma Impugnação para documentos específicos.',
  },
  {
    name: 'Sustentação Oral',
    description: 'Crie Sustentação Oral com base em Recursos e Contrarrazões.',
  },
  {
    name: 'Defesa em Execução',
    description: 'Elabore Defesa em Execução com base em fatos e petições.',
  },
  {
    name: 'Defesa Penal',
    description: 'Desenvolva uma Defesa Penal a partir de uma Decisão.',
  },
  {
    name: 'Minuta de Decisão',
    description: 'Crie Decisões completas baseadas em resumos e argumentos.',
  },
  {
    name: 'Analisador de Petição',
    description: 'Analise Petições e confira se são ideais para o caso.',
  },
  {
    name: 'Análise Legislativa',
    description: 'Descubra legislações relacionadas ao seu caso.',
  },
  {
    name: 'Consulta de Jurisprudências',
    description: 'Busque jurisprudências relevantes ao seu caso.',
  },
  {
    name: 'Crie Perguntas Inteligentes',
    description: 'Gere perguntas incomuns para fazer ao seu cliente sobre o caso em questão.',
  },
  {
    name: 'Emoções e Padrões Ocultos',
    description: 'Descubra padrões sentimentais subjetivos e vieses ocultos de textos.',
  },
  {
    name: 'Análise de Contrato',
    description: 'Analise um contrato inteiro extraindo pontos fracos a serem otimizados e clareados.',
  },
  {
    name: 'Análise Jurídica Prévia',
    description: 'Faça uma análise avançada do seu caso, e descubra insights importantes para resolução do caso.',
  },
  {
    name: 'Gerador de Estratégia de Caso',
    description: 'Gere estratégias eficazes para seu caso a partir de uma narrativa de fatos.',
  },
  {
    name: 'Avaliador de Riscos de Estratégias',
    description: 'Avalie os riscos de sua estratégia e descubra possíveis formas de melhorá-la.',
  },
  {
    name: 'Gerador de Estratégias de Negociação',
    description: 'Gere estratégias de negociação eficazes para seu caso a partir da narrativa de fatos.',
  },
  {
    name: 'Passo a Passo para o Caso',
    description: 'Descubra o passo a passo ideal para resolver seu caso da melhor forma possível!',
  },
  {
    name: 'Documentos Necessários',
    description: 'Descubra os principais documentos que você precisa para ter a melhor performance no seu caso.',
  },
  {
    name: 'Resumo de Documento',
    description: 'Tenha um resumo detalhado sobre qualquer documento, extraindo apenas as informações relevantes.',
  },
  {
    name: 'Minuta de Contrato',
    description: 'Gere minutas de contrato com base em termos e condições.',
  },
  {
    name: 'Parecer Jurídico',
    description: 'Crie pareceres jurídicos baseados em narrativas de fatos.',
  },
  {
    name: 'Análise e Aprimoramento de Texto',
    description: 'Aprimore textos com base em objetivos específicos.',
  },
  {
    name: 'Análise de Ortografia e Gramática',
    description: 'Analise textos quanto à ortografia e gramática de forma avançada.',
  },
  {
    name: 'Simplifique o "Juridiquês"',
    description: 'Simplifique termos jurídicos para facilitar a comunicação.',
  },
  {
    name: 'Preparação da Testemunha',
    description: 'Prepare testemunhas prevendo perguntas críticas da audiência.',
  },
  {
    name: 'Perguntas para Testemunha Contrária',
    description: 'Descubra perguntas críticas para a testemunha contrária.',
  },
  {
    name: 'Preparação da Parte',
    description: 'Prepare a parte para perguntas críticas durante a audiência.',
  },
  {
    name: 'Perguntas para Parte Contrária',
    description: 'Elabore perguntas críticas para a parte contrária.',
  },
  {
    name: 'Decisões Possíveis',
    description: 'Descubra possíveis decisões com base no processo e provas.',
  },
  {
    name: 'Argumentação Jurídica',
    description: 'Crie argumentações jurídicas com base em fatos e objetivos.',
  },
];

const ToolCard = ({ name, description }: typeof tools[0]) => (
  <div className="group flex flex-col items-center p-6 rounded-xl mx-4 min-w-[280px]
    relative backdrop-blur-lg bg-white/5 border border-white/10
    transition-all duration-500 ease-out
    hover:transform hover:-translate-y-2
    hover:bg-white/15 hover:border-[#28E7C5]/40
    hover:shadow-[0_0_30px_rgba(40,231,197,0.2)]
    before:absolute before:inset-0 
    before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent
    before:opacity-0 before:transition-opacity before:duration-500
    hover:before:opacity-100
    cursor-grab active:cursor-grabbing">
    <h3 className="text-xl font-semibold mb-2 text-center text-white transition-colors duration-500 group-hover:text-[#28E7C5]">{name}</h3>
    <p className="text-gray-300 text-center transition-colors duration-500 group-hover:text-white">{description}</p>
    <div className="absolute -inset-px bg-gradient-to-r from-[#28E7C5]/0 via-[#28E7C5]/10 to-[#28E7C5]/0 
                   opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none" />
  </div>
);

export default function Tools() {
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const x = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);

  // Triple the tools array for seamless infinite scroll
  const repeatedTools = [...tools, ...tools, ...tools];
  const carouselWidth = tools.length * 300; // Width of one complete set
  const totalWidth = carouselWidth * 3; // Total width of all three sets

  const resetPosition = (currentPosition: number) => {
    // If we've scrolled past the second set, reset to the first set
    if (currentPosition < -carouselWidth * 2) {
      const newPosition = currentPosition + carouselWidth;
      x.set(newPosition);
      return newPosition;
    }
    // If we've scrolled back past the first set, reset to the second set
    if (currentPosition > -carouselWidth) {
      const newPosition = currentPosition - carouselWidth;
      x.set(newPosition);
      return newPosition;
    }
    return currentPosition;
  };

  const startAnimation = (fromPosition = 0) => {
    const duration = 120;
    const adjustedPosition = resetPosition(fromPosition);
    const targetPosition = adjustedPosition - carouselWidth;
    
    controls.start({
      x: [adjustedPosition, targetPosition],
      transition: {
        duration: duration,
        repeat: Infinity,
        ease: "linear",
        repeatType: "loop",
        times: [0, 1],
        onComplete: () => {
          const finalPos = x.get();
          resetPosition(finalPos);
        },
      },
    });
  };

  const handleDragStart = () => {
    setIsDragging(true);
    controls.stop();
    
    const currentPosition = x.get();
    const adjustedPosition = resetPosition(currentPosition);
    
    controls.set({ x: adjustedPosition });
    x.stop();
    x.set(adjustedPosition);
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    const velocity = info.velocity.x;
    const currentPosition = x.get();
    const adjustedPosition = resetPosition(currentPosition);
    
    if (Math.abs(velocity) > 100) {
      const momentumDistance = velocity * 0.5;
      const targetPosition = adjustedPosition + momentumDistance;
      const decelerationDuration = Math.min(Math.abs(velocity) * 0.002, 1.5);
      
      controls.start({
        x: [
          adjustedPosition,
          targetPosition,
          targetPosition - carouselWidth
        ],
        transition: {
          duration: 120,
          times: [
            0,
            decelerationDuration / 120,
            1
          ],
          ease: [
            "easeOut",
            "linear"
          ]
        }
      }).catch(() => {
        const interruptPosition = x.get();
        startAnimation(resetPosition(interruptPosition));
      });
    } else {
      startAnimation(adjustedPosition);
    }
  };

  useEffect(() => {
    startAnimation(-carouselWidth); // Start from the middle set
    return () => {
      controls.stop();
    };
  }, []);

  return (
    <section
      className="py-16 relative overflow-hidden"
      id="tools"
      style={{
        backgroundColor: '#081428',
      }}
    >
      <div className="container mx-auto px-6 mb-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Nossas Ferramentas
          </h2>
          <p className="text-xl text-gray-300">
            Feitas por advogados, para advogados
          </p>
        </div>
      </div>

      <div className="relative w-full overflow-hidden py-8" ref={containerRef}>
        <div className="absolute inset-0 pointer-events-none" style={{ height: "calc(100% + 2rem)" }} />
        <motion.div
          className="flex"
          drag="x"
          dragConstraints={{
            left: -totalWidth + (containerRef.current?.offsetWidth || 0),
            right: 0,
          }}
          dragElastic={0.2}
          dragMomentum={true}
          dragTransition={{
            power: 0.3,
            timeConstant: 200,
            modifyTarget: (target) => target
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          animate={controls}
          style={{
            x,
            width: `${totalWidth}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onHoverStart={() => {
            controls.stop();
            const currentPosition = x.get();
            const adjustedPosition = resetPosition(currentPosition);
            x.set(adjustedPosition);
          }}
          onHoverEnd={() => {
            if (!isDragging) {
              const currentPosition = x.get();
              startAnimation(resetPosition(currentPosition));
            }
          }}
          onViewportEnter={() => {
            if (!isDragging) {
              const currentPosition = x.get();
              startAnimation(resetPosition(currentPosition));
            }
          }}
        >
          {repeatedTools.map((tool, index) => (
            <ToolCard key={`${tool.name}-${index}`} {...tool} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}